import { useCallback, useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { graphql } from '@tn/foundation/graphql/__generated/gql';
import type { FullUser } from '@tn/foundation/graphql/__generated/resolvers-types';
import useFullUser from '@tn/foundation/hooks/providers/useFullUser';
import { prefetchRequest, useRequest } from '@tn/foundation/utils/request';
import type { IncomingMessage } from 'node:http';

// P+ only: these FullUser/PackageInfo fields exist in the app-shell user-profiles schema, not
// PTV's — never add them to the shared UserFullProfile query (useFullUser).
const query = graphql(/* GraphQL */ `
    query VideoAdUserProfile {
        userFullProfile {
            adsRegion
            ppid
            ageGroup
            gender
            sl
            cbsPackageInfo {
                packageSourceType
                vendorUserType
            }
        }
    }
`);

// Caps how long an ad request waits on the profile before falling back to empty targeting.
export const AD_USER_WAIT_TIMEOUT_MS = 3000;

const AD_USER_CACHE_KEY = 'videoAdUserProfile';

/** Scoped to the active profile, so an identity change fetches that identity's fields. */
export const buildAdUserCacheKey = (profileId?: string | null) =>
    `${AD_USER_CACHE_KEY}-${profileId ?? 'anon'}`;

/** Used with getServerSideProps, keyed like the hook from the SSR full user. */
export const prefetchPPlusVideoAdUser =
    (userFullProfile?: Pick<FullUser, 'activeProfile'> | null) => (req: IncomingMessage) =>
        prefetchRequest({
            cacheKey: buildAdUserCacheKey(userFullProfile?.activeProfile?.id),
            query,
            appName: 'user-profiles',
            req,
        });

/** Drops every cached identity's fields (auth/profile refresh). */
export const invalidatePPlusVideoAdUser = (queryClient: QueryClient) =>
    queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
            typeof queryKey[0] === 'string' && queryKey[0].startsWith(AD_USER_CACHE_KEY),
    });

/** User fields legacy web sent Smart Tag as video ad session data (see buildPPlusSiteSessionData). */
const usePPlusVideoAdUser = (enabled = true) => {
    const { activeProfile, isLoading: isUserLoading } = useFullUser();
    const { data, status } = useRequest({
        cacheKey: buildAdUserCacheKey(activeProfile?.id),
        query,
        appName: 'user-profiles',
        // These fields only change with the identity (re-keyed) or an auth refresh (invalidated).
        requestOptions: { enabled: enabled && !isUserLoading, staleTime: Infinity },
    });
    const settled = !enabled || (!isUserLoading && status !== 'pending');
    const settledRef = useRef(settled);
    settledRef.current = settled;
    const waitersRef = useRef<Array<() => void>>([]);

    useEffect(() => {
        if (!settled) return;
        waitersRef.current.forEach((resolve) => resolve());
        waitersRef.current = [];
    }, [settled]);

    // Resolves once the current identity's query succeeded or failed (or after the timeout), so
    // callers read real PPID/targeting instead of the pending-state null.
    const waitForAdUser = useCallback(
        (): Promise<void> =>
            settledRef.current
                ? Promise.resolve()
                : new Promise((resolve) => {
                      waitersRef.current.push(resolve);
                      setTimeout(resolve, AD_USER_WAIT_TIMEOUT_MS);
                  }),
        [],
    );

    return { adUser: data?.userFullProfile ?? null, waitForAdUser };
};

export default usePPlusVideoAdUser;
